// FILECONTENT_START src/common/components/DataGrid/useGrid.ts
import {
  GridEnrichedColDef,
  GridSelectionModel,
  GridSortItem,
  GridValidRowModel,
  GridValueFormatterParams,
} from "@mui/x-data-grid-pro";
import { AxiosRequestConfig } from "axios";
import I18n from "i18n-js";
import { get, omit, pick, size } from "lodash";
import moment from "moment-timezone";
import QueryString, { default as qs } from "qs";
import { useCallback, useEffect, useRef, useState } from "react";
import { SaveFormProps } from "../Form";
import { UseDialogReturnProps, useDialog } from "./../Dialog";
import { globalMessageHandler } from "./../GlobalMessaging";
import {
  GridColumns,
  GridFilterModel,
  GridRowModel,
  GridRowParams,
  GridSortModel,
  GridValueGetterParams,
} from "./grid-imports";

export interface GridRowsModel<
  T extends GridValidRowModel = GridValidRowModel
> {
  rows: GridRowModel<T>[];
  rowCount: number;
  totals?: any;
}
export interface GridPagingModel {
  page: number;
  pageSize: number;
}

const defaultGridRowsState: GridRowsModel = {
  rowCount: 0,
  rows: [],
};
const defaultGridPagingState: GridPagingModel = {
  page: 0,
  pageSize: 20,
};

const defaultGridFilterState: GridFilterModel = {
  items: [],
};

function transformServerResponse(
  response: any
): GridRowsModel & GridPagingModel {
  return {
    rowCount: response.total,
    rows: response.data || [],
    page: response.current_page - 1,
    pageSize: response.per_page,
    totals: response.totals,
  };
}
export type TrashTypes = "onlyTrashed" | "withTrashed";

interface GridRequestParams {
  pagingState: GridPagingModel;
  sortState: GridSortModel;
  filterState: GridFilterModel;
  searchText: string;
  trashType?: TrashTypes;
  options?: any;
}

function getFilterState(item: any) {
  let state: GridFilterModel = {
    items: [],
  };
  Object.keys(item).map((key: string) => {
    state.items.push({
      id: key,
      columnField: key,
      value: typeof item[key] === "boolean" ? String(item[key]) : item[key],
      operatorValue: "equals",
    });
  });
  return state;
}

function getFilterItem(filterState: GridFilterModel) {
  let filter = {} as any;
  filterState.items?.forEach(item => {
    filter[item.columnField] =
      item.value === "true"
        ? true
        : item.value === "false"
        ? false
        : item.value;
  });
  return filter;
}

function transformRequest(
  {
    pagingState,
    sortState,
    filterState,
    searchText,
    options,
  }: GridRequestParams,
  comaSeparatedSortPrarms: boolean
): any {
  return {
    page: pagingState.page + 1,
    "per-page": pagingState.pageSize,
    //filter: filterState.items,
    keyword: searchText && searchText !== "" ? searchText : undefined,
    sort: sortState
      ? comaSeparatedSortPrarms
        ? sortState
            .map(sort => `${sort.sort === "desc" ? "-" : ""}${sort.field}`)
            .join(",")
        : sortState.map(
            sort => `${sort.sort === "desc" ? "-" : ""}${sort.field}`
          )
      : undefined,
    options,
    ...getFilterItem(filterState),
  };
}

function getRealtionValue(params: GridValueGetterParams) {
  return get(params.row, params.field);
}

function getOptionLabel(params: GridValueGetterParams) {
  return params.colDef.valueOptions
    ? (params.colDef.valueOptions as any[]).find(
        option => option.value === params.row[params.field]
      )?.label
    : "";
}
//depricated start
export function percentValueGetter(params: GridValueFormatterParams) {
  //console.warn("percentValueGetter is depricated use percentValueFormatter");
  return params.value
    ? I18n.toPercentage(parseFloat(params.value as any) * 100)
    : null;
}
export function timestampValueGetter(params: GridValueFormatterParams) {
  /*console.warn(
    "timestampValueGetter is depricated use timestampValueFormatter"
  );*/
  return params.value ? moment(params.value as any).format("L LT") : null;
}
//depricated end
export function dateValueGetter(params: GridValueFormatterParams) {
  //console.warn("dateValueGetter is depricated use dateValueFormatter");
  return params.value ? moment(params.value as any).format("L") : null;
}
export function percentValueFormatter(params: GridValueFormatterParams) {
  return params.value
    ? I18n.toPercentage(parseFloat(params.value as any) * 100)
    : null;
}
export function dateTimeValueFormatter(params: GridValueFormatterParams) {
  return params.value ? moment(params.value as any).format("L LT") : null;
}
export function dateValueFormatter(params: GridValueFormatterParams) {
  return params.value ? moment(params.value as any).format("L") : null;
}
export function currencyValueFormatter(params: GridValueFormatterParams) {
  return params.value ? I18n.toCurrency(Number(params.value)) : null;
}

type DataMode = "client" | "server";
export interface UseDataGridProps<T = any, L = any, F = any, O = any> {
  loadItems(queryParams: string, onlyTrashed?: boolean): Promise<any>;
  loadOptions?: (
    requestConfig?: AxiosRequestConfig,
    component?: string
  ) => Promise<any>;
  deleteItems?: (ids: any[], onlyTrashed?: boolean) => Promise<any>;
  restoreItems?: (ids: any[]) => Promise<any>;
  deleteItem?(id: any, onlyTrashed?: boolean): Promise<SaveFormProps<T>>;
  restoreItem?: (id: any) => Promise<SaveFormProps<T>>;
  updateItem?(
    id: number,
    item: T,
    onlyTrashed?: boolean
  ): Promise<SaveFormProps<T>>;
  createItem?(item: T): Promise<SaveFormProps<T>>;
  id: string;
  translationCategory?: string;
  filterTranslationCategory?: string;
  defaultSortState?: GridSortModel;
  defaultFilterState?: GridFilterModel;
  defaultFilter?: Partial<F>;
  defaultPageSize?: number;
  canHandleTrash?: boolean;
  refreshRef?: Function;
  exportFilter?: (filter: any) => any;
  pageSizeUrlParam?: string;
  pageUrlParam?: string;
  keywordUrlParam?: string;
  sortUrlParam?: string;
  confirmAttribute?: string;
  primaryKey?: string;
  optionComponent?: string;
  optionParams?: (filter: any) => any;
  loadColumns?: boolean;
  clientMode?: boolean;
  storageType?: "urlParams" | "sessionStorage" | "none";
  comaSeparatedSortPrarms?: boolean;
  multipleGridOnPage?: boolean;
  paginationMode?: DataMode;
  sortingMode?: DataMode;
  filterMode?: DataMode;
  beforeLoad?: (filter: F) => Promise<any>;
  afterDelete?: Function;
  afterLoad?: (newRowsModel: GridRowsModel) => void;
  onSelectionModelChange?: (newSelectionModel: GridSelectionModel) => void;
  attributesNotCount?: string[];
  options?: O;
}

function getStorage(id: string) {
  function getState(): any {
    const json = sessionStorage.getItem(`grid.${id}`);
    if (json) {
      return JSON.parse(json);
    }
    return;
  }

  function setState(item?: any) {
    if (item) {
      sessionStorage.setItem(`grid.${id}`, JSON.stringify(item));
    }
  }
  return {
    getState,
    setState,
  };
}

function getNoneStorage() {
  function getState(): any {
    return;
  }

  function setState(item?: any) {}
  return {
    getState,
    setState,
  };
}

function getSortItem(str: string): GridSortItem {
  if (str.substring(0, 1) === "-") {
    return { field: str.substring(1), sort: "desc" };
  } else {
    return { field: str, sort: "asc" };
  }
}
function getRouterStorage(id?: string) {
  //Url params as object
  let _params: any = QueryString.parse(window.location.search.substring(1), {
    //parseArrays: true,
  });

  //Remove id from params if exists
  const params: any = id ? _params[id] || {} : _params || {};

  function getState(): Partial<GridRequestParams> {
    const state: Partial<GridRequestParams> = {};
    if (params.page && params.pageSize) {
      state.pagingState = {
        page: Number(params.page) - 1,
        pageSize: Number(params.pageSize),
      };
    }

    if (params.sort) {
      if (Array.isArray(params.sort)) {
        state.sortState = params.sort.map((s: string) => getSortItem(s));
      } else if (params.sort) {
        state.sortState = [getSortItem(params.sort)];
      }
      //state.sortState= params.
    }
    if (params.options) {
      state.options = params.options;
    }

    state.searchText = params.search;
    const _state = getFilterState(
      omit(params, ["page", "pageSize", "search", "sort", "options"])
    );
    if (_state.items.length > 0) {
      state.filterState = _state;
    }

    return state;
  }

  function setState(state: GridRequestParams) {
    let _params = {
      ...state.pagingState,
      pageSize: state.pagingState.pageSize,
      page: state.pagingState.page + 1,
      sort: state.sortState
        ? state.sortState.map(
            sort => `${sort.sort === "desc" ? "-" : ""}${sort.field}`
          )
        : undefined,
      ...getFilterItem(state.filterState),
      search:
        state.searchText && state.searchText !== ""
          ? state.searchText
          : undefined,
      options: state.options,
    };

    const params = id ? { [id]: _params } : _params;

    const query = QueryString.stringify(params, {
      encode: false,
      //arrayFormat: "repeat",
    });

    window.history.replaceState(
      {},
      "",
      query && query !== "" ? `?${query}` : "?"
    );
  }
  return {
    getState,
    setState,
  };
}

export function arrayToGridRowsState(data?: any[]) {
  return {
    rows: data || [],
    totals: size(data),
    rowCount: size(data),
  };
}

export type UseGridReturnType<T = any, L = any, F = any, O = any> = {
  editDialogHook: UseDialogReturnProps;
  sortingMode?: DataMode;
  filterMode?: DataMode;
  paginationMode?: DataMode;
  columns: GridColumns;
  loading: boolean;
  load: () => void;
  deleteSelectedItems: () => void;
  restoreSelectedItems: () => void;
  deleteItem: (item: any) => void;
  restoreItem: (item: any) => void;
  updateItem?: (
    id: number,
    item: T,
    onlyTrashed?: boolean | undefined
  ) => Promise<SaveFormProps<T>>;
  setItem: (item: T) => void;
  createItem?: (item: T) => Promise<SaveFormProps<T>>;
  setTrashType: (trashType: TrashTypes) => void;
  trashType?: TrashTypes;
  //...rowsState,
  rows: L[];
  rowCount: number;
  totals?: any;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /*onFilterModelChange: (filterModel: any) =>
      onFilterModelChangeHack.current === false &&
      handleFilterChange(filterModel),*/
  onSortModelChange: (sortModel: any) => void;
  selectionModel: GridSelectionModel;
  onSelectionModelChange: (selectionModel: GridSelectionModel) => void;
  prepareColumns: (
    columns: GridColumns,
    getActions?: (params: GridRowParams) => React.ReactElement[]
  ) => GridColumns;
  applyFilter: (item: F) => void;
  applySettings: (item: F) => void;
  resetFilter: () => void;
  resetSettings: () => void;
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  canHandleTrash?: boolean;
  createTitle: string;
  editTitle: string;
  toolbarProps: any;
  setSelectionState: (selection: GridSelectionModel) => void;
  /* toolbarProps: {
      createTitle: I18n.t(`${translationCategory}.addTitle`, {
        defaultValue: I18n.t("App.addNew"),
      }),
      editTitle: I18n.t(`${translationCategory}.title`, {
        defaultValue: I18n.t("App.addNew"),
      }),
      attributesNotCount,
      load,
      optionComponent,
      translationCategory: filterTranslationCategory,
      rowsState,
      selectionState,
      filter: getFilterItem(state.filterState),
      settings: state.options,
      options,
      resetFilter,
      resetSettings,
      applyFilter,
      applySettings,
      loadOptions,
      value: state.searchText,
      onChange: (event: any) =>
        setState({ ...state, searchText: (event?.target?.value as any) || "" }),
      clearSearch: () => setState({ ...state, searchText: "" }),
    };*/
} & GridPagingModel; // & GridSelectionModel; // & GridRowsModel<L = any>;

export default function useGrid<
  T = any,
  L extends GridValidRowModel = GridValidRowModel,
  F = any,
  O = any
>({
  id,
  loadItems,
  deleteItem,
  deleteItems,
  restoreItem,
  restoreItems,
  translationCategory,
  defaultSortState,
  defaultFilterState,
  defaultFilter,
  defaultPageSize,
  canHandleTrash,
  refreshRef,
  exportFilter,
  beforeLoad,
  comaSeparatedSortPrarms = false,
  pageSizeUrlParam = "per-page",
  pageUrlParam = "page",
  keywordUrlParam = "keyword",
  sortUrlParam = "sort",
  loadOptions,
  confirmAttribute,
  primaryKey = "id",
  optionComponent = "grid",
  updateItem,
  createItem,
  loadColumns,
  filterTranslationCategory,
  clientMode,
  onSelectionModelChange: _onSelectionModelChange,
  storageType = "sessionStorage",
  multipleGridOnPage,
  paginationMode,
  sortingMode,
  filterMode,
  attributesNotCount,
  afterDelete,
  afterLoad,
  options: _options,
  optionParams,
}: UseDataGridProps<T, L, F, O>): UseGridReturnType<T, L, F, O> {
  const storage =
    storageType === "urlParams"
      ? getRouterStorage(multipleGridOnPage ? id : undefined)
      : storageType === "none"
      ? getNoneStorage()
      : getStorage(id);

  const timeout = useRef<any>();
  const [modalId, setModalId, modal] = useDialog<"create" | T>();
  //onFilterModelChange is triggered by the grid on start twice with empty filter and overrides storage parameters
  const onFilterModelChangeHack = useRef<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState<GridColumns>([]);
  const [options, setOptions] = useState<O>({ ..._options } as O);
  const [state, setState] = useState<GridRequestParams>({
    filterState: {
      ...defaultGridFilterState,
      ...defaultFilterState,
      ...(defaultFilter ? getFilterState(defaultFilter) : {}),
    },
    sortState: defaultSortState || [],
    pagingState: {
      ...defaultGridPagingState,
      ...(defaultPageSize ? { pageSize: defaultPageSize } : {}),
    },
    searchText: "",
    ...storage.getState(),
  });

  const [rowsState, setRowsState] = useState<GridRowsModel<L>>(
    defaultGridRowsState as any
  );
  const [selectionState, setSelectionState] = useState<GridSelectionModel>([]);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);

  function resetFilter() {
    if (attributesNotCount) {
      let defState = { items: [...defaultGridFilterState.items] };

      state.filterState.items.forEach(item => {
        if (attributesNotCount.includes(item.columnField)) {
          defState.items.push(item);
        }
      });
      setState({ ...state, filterState: defState, searchText: "" });
    } else {
      setState({
        ...state,
        filterState: defaultGridFilterState,
        searchText: "",
      });
    }
  }

  function resetSettings() {
    setState({
      ...state,
      options: {},
    });
  }

  function applyFilter(item: F) {
    onFilterModelChangeHack.current = true;
    const filterState = getFilterState(omit(item as any, keywordUrlParam));
    const searchText: string = (item as any)[keywordUrlParam] || "";
    setState({ ...state, filterState, searchText });
  }

  function applySettings(options: any) {
    setState({ ...state, options });
  }

  const loadData = useCallback(
    async (requestParams: GridRequestParams) => {
      setLoading(true);
      if (beforeLoad) {
        if ((await beforeLoad(getFilterItem(state.filterState))) === false) {
          return;
        }
      }

      let filter = transformRequest(requestParams, comaSeparatedSortPrarms);
      let _filter = (await exportFilter)
        ? {
            ...pick(filter, [
              keywordUrlParam,
              pageSizeUrlParam,
              pageUrlParam,
              sortUrlParam,
            ]),
            ...(exportFilter
              ? await exportFilter(
                  omit(filter, [
                    keywordUrlParam,
                    pageSizeUrlParam,
                    pageUrlParam,
                    sortUrlParam,
                  ])
                )
              : {}),
          }
        : filter;

      const data = await loadItems(
        clientMode
          ? ""
          : qs.stringify(_filter, {
              encode: false,
            }),
        requestParams?.trashType === "onlyTrashed"
      );

      if (data) {
        if (!clientMode) {
          //storage.setStorageData("filter", requestParams.filterState);
          //storage.setStorageDataScalar("search", requestParams.searchText);
          //storage.setStorageData("paging", requestParams.pagingState);
          //storage.setStorageData("sort", requestParams.sortState);
        }
        let tData: any = transformServerResponse(data);
        //const _pagingState: GridPagingModel = pick(tData, ["page", "pageSize"]);

        //Handle data on client side pagination (no meta data from server)
        if (paginationMode !== "server" && Array.isArray(data || [])) {
          tData = arrayToGridRowsState(data);
        }

        const _rowsState: GridRowsModel<L> = pick(tData, [
          "rows",
          "rowCount",
          "totals",
        ]);
        /*if (!_pagingState.page) {
          _pagingState.page = pagingState.page;
        }
        if (!_pagingState.pageSize) {
          _pagingState.pageSize = pagingState.pageSize;
        }
        if (!_rowsState.rows) {
          _rowsState.rows = rowsState.rows;
        }
        if (!_rowsState.rowCount) {
          _rowsState.rowCount = rowsState.rowCount;
        }
        if (!clientMode) {
          //storage.setStorageData("paging", pagingState);
        }
        if (clientMode) {
          _rowsState.rowCount = undefined as any;
        }*/
        setRowsState(_rowsState);
        afterLoad && afterLoad(_rowsState);
        /*if (JSON.stringify(pagingState) !== JSON.stringify(_pagingState)) {
          setPagingState(_pagingState);
        }*/
      }
      setLoading(false);
    },
    [
      /*pagingState*/
      //beforeLoad,
    ]
  );

  function load() {
    loadData(state);
  }

  const handleFilterChange = (filterModel: GridFilterModel) => {
    setState({ ...state, filterState: filterModel });
  };

  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      setState({ ...state, sortState: newModel });
    },
    [state]
  );

  function handlePageChange(page: number) {
    setState({ ...state, pagingState: { ...state.pagingState, page } });
  }

  function handlePageSizeChange(pageSize: number) {
    setState({ ...state, pagingState: { ...state.pagingState, pageSize } });
  }

  function getVelueGetter(column: GridEnrichedColDef): any {
    if (column.valueGetter) {
      return column.valueGetter;
    }

    if (column.field.includes(".")) {
      return getRealtionValue;
    }

    if (column.type === "date") {
      return dateValueFormatter;
    } else if (column.type === "datetime") {
      return dateTimeValueFormatter;
    } else if (column.type === "timestamp") {
      return dateTimeValueFormatter;
    } else if (column.type === "currency") {
      return currencyValueFormatter;
    } else if (column.type === "percent") {
      return percentValueFormatter;
    }
  }

  function prepareColumns(
    columns: GridColumns,
    getActions?: (params: GridRowParams) => React.ReactElement[]
  ): GridColumns {
    let gridColumns: GridColumns = columns.map((column: any) => ({
      ...column,
      //Relation
      valueGetter: getVelueGetter(column),
      renderCell:
        !column.renderCell && column.type === "singleSelect"
          ? getOptionLabel
          : column.renderCell,
      //Translation
      headerName:
        translationCategory && !column.headerName
          ? I18n.t(
              `${translationCategory}.attributes.${column.field.replace(
                /\./g,
                "_"
              )}`,
              {
                defaultValue: column.field,
              }
            )
          : column.headerName,
    }));
    if (getActions) {
      const actions = getActions({} as GridRowParams);

      gridColumns.push({
        field: "actions",
        type: "actions",
        headerName: I18n.t("App.actions"),
        width: actions.length * 44 + (state.trashType ? 44 : 0) + 44,
        align: "right",
        getActions,
      });
    }

    return gridColumns;
  }

  function handleSelectionModelChange(newSelectionModel: GridSelectionModel) {
    setSelectionState(newSelectionModel);
  }

  function deleteSelectedItems() {
    globalMessageHandler.confirm(
      I18n.t(
        state.trashType === "onlyTrashed"
          ? "App.deleteSelectedTrashConfirm"
          : "App.deleteSelectedConfirm",
        { count: selectionState.length }
      ),
      async () => {
        if (deleteItems) {
          const result = await deleteItems(
            selectionState,
            state.trashType === "onlyTrashed"
          );
          if (result.success) {
            setSelectionState([]);
            load();
          }
          return result;
        }
      }
    );
  }

  function restoreSelectedItems() {
    globalMessageHandler.confirm(
      I18n.t("App.restoreSelectedConfirm", { count: selectionState.length }),
      async () => {
        if (restoreItems) {
          const result = await restoreItems(selectionState);
          if (result.success) {
            setSelectionState([]);
            load();
          }
          return result;
        }
      }
    );
  }

  async function _deleteItem(item: any) {
    const id = item[primaryKey];

    let attributeValue = id;
    if (confirmAttribute) {
      attributeValue = get(item, confirmAttribute);
    } else if (columns[0]) {
      attributeValue = item[columns[0].field];
    }
    /*if (attributeValue !== id) {
      const d = moment(attributeValue);
      if (d.isValid()) {
        attributeValue = d.format("L");
      }
    }*/

    globalMessageHandler.confirm(
      I18n.t("App.deleteItemConfirm", {
        item: `${attributeValue}`,
      }),
      async () => {
        if (deleteItem) {
          const { success } = await deleteItem(
            id,
            state.trashType === "onlyTrashed"
          );
          if (success) {
            afterDelete && afterDelete();
            globalMessageHandler.success(I18n.t("App.successDeleted"));
            load();
          }
        }
      }
    );
  }

  async function _restoreItem(item: any) {
    const id = item[primaryKey];
    globalMessageHandler.confirm(
      I18n.t("App.restoreItemConfirm", {
        item: `${confirmAttribute ? get(item, confirmAttribute) : id}`,
      }),
      async () => {
        if (restoreItem) {
          const { success } = await restoreItem(id);
          if (success) {
            globalMessageHandler.success(I18n.t("App.successRestored"));
            load();
          }
        }
      }
    );
  }

  function onSelectionModelChange(selectionModel: GridSelectionModel) {
    handleSelectionModelChange(selectionModel);
    if (_onSelectionModelChange) {
      _onSelectionModelChange(selectionModel);
    }
  }

  async function _loadOptions(params?: any) {
    console.log("LOAD OPTIONS");
    if (loadOptions) {
      const options = await loadOptions(
        {
          /*cache: { ignoreCache: false }*/
          params: optionParams
            ? optionParams(getFilterItem(state.filterState))
            : undefined,
          ...params,
        },
        optionComponent
      );
      setColumns(options?.columns || []);
      setOptions(options?.options || {});
    }
  }

  function setItem(item: T) {
    let newRows = [...rowsState.rows];
    const index = newRows.findIndex(
      i => i[primaryKey] === (item as any)[primaryKey]
    );
    if (index >= 0) {
      newRows[index] = item as any;
      setRowsState({ ...rowsState, rows: newRows });
    }
  }

  function handleStateChange() {
    storage.setState(state);
    if (!clientMode) {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => {
        loadData(state);
      }, 500);
    }
    refreshRef && refreshRef(load);
  }
  useEffect(() => {
    if (filterMode !== "client") {
      handleStateChange();
      setTimeout(() => {
        onFilterModelChangeHack.current = false;
      }, 1000);
    }
  }, [state.filterState]);
  useEffect(() => {
    handleStateChange();
  }, [state.searchText]);
  useEffect(() => {
    handleStateChange();
  }, [state.options]);
  useEffect(() => {
    if (sortingMode !== "client") {
      handleStateChange();
    }
  }, [state.sortState]);
  useEffect(() => {
    if (paginationMode !== "client") {
      handleStateChange();
    }
  }, [state.pagingState]);
  useEffect(() => {
    handleStateChange();
  }, [loadData]);

  useEffect(() => {
    if (clientMode) {
      loadData(state);
    }
    setTimeout(() => {
      onFilterModelChangeHack.current = false;
    }, 1000);
  }, []);
  useEffect(() => {
    if (onFilterModelChangeHack.current === false) {
      loadData(state);
    }
  }, [state.trashType]);

  useEffect(() => {
    if (loadColumns) {
      _loadOptions();
    }
  }, [loadColumns]);
  useEffect(() => {
    console.log("LOL");
    if (filterOpen) {
      console.log("Filter changed", state.filterState);
      _loadOptions();
    }
  }, [filterOpen]);

  useEffect(() => {
    setOptions(_options || ({} as O));
  }, [_options]);

  let grid: UseGridReturnType<T, L, F, O> = {
    editDialogHook: [modalId, setModalId, modal] as UseDialogReturnProps,
    sortingMode,
    filterMode,
    paginationMode,
    columns,
    loading,
    load,
    deleteSelectedItems,
    restoreSelectedItems,
    deleteItem: _deleteItem,
    restoreItem: _restoreItem,
    updateItem,
    createItem,
    setItem,
    setTrashType: (trashType: TrashTypes) => setState({ ...state, trashType }),
    setSelectionState,
    trashType: state.trashType,
    ...state.pagingState,
    selectionModel: selectionState,
    ...rowsState,
    filterModel: state.filterState,
    sortModel: state.sortState,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    /*onFilterModelChange: (filterModel: any) =>
      onFilterModelChangeHack.current === false &&
      handleFilterChange(filterModel),*/
    onSortModelChange: (sortModel: any) =>
      onFilterModelChangeHack.current === false &&
      handleSortModelChange(sortModel),
    onSelectionModelChange,
    prepareColumns,
    applyFilter,
    applySettings,
    resetFilter,
    resetSettings,
    filterOpen,
    setFilterOpen,
    canHandleTrash,
    createTitle: I18n.t(`${translationCategory}.addTitle`, {
      defaultValue: I18n.t("App.addNew"),
    }),
    editTitle: I18n.t(`${translationCategory}.title`, {
      defaultValue: I18n.t("App.addNew"),
    }),
    toolbarProps: {
      createTitle: I18n.t(`${translationCategory}.addTitle`, {
        defaultValue: I18n.t("App.addNew"),
      }),
      editTitle: I18n.t(`${translationCategory}.title`, {
        defaultValue: I18n.t("App.addNew"),
      }),
      attributesNotCount,
      load,
      optionComponent,
      translationCategory: filterTranslationCategory,
      rowsState,
      selectionState,
      filter: getFilterItem(state.filterState),
      settings: state.options,
      options,
      resetFilter,
      resetSettings,
      applyFilter,
      applySettings,
      loadOptions,
      value: state.searchText,
      onChange: (event: any) =>
        setState({ ...state, searchText: (event?.target?.value as any) || "" }),
      clearSearch: () => setState({ ...state, searchText: "" }),
    },
  };

  if (!sortingMode) {
    delete grid.sortingMode;
  }
  if (!filterMode) {
    delete grid.filterMode;
  }
  if (!paginationMode) {
    delete grid.paginationMode;
  }

  return grid;
}

// FILECONTENT_END src/common/components/DataGrid/useGrid.ts
